import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  User, Building2, Shield, Bell, AlertTriangle, Eye, EyeOff, Save, X, Camera,
  Trash2, LogOut, CheckCircle2, Info, KeyRound,
} from "lucide-react";
import { toast } from "sonner";

const VALID_TABS = ["profile", "school", "security", "notifications", "danger"];

const PROFILE_FIELDS = [
  { key: "prenom",           label: "Prénom",            type: "text" },
  { key: "nom",              label: "Nom",               type: "text" },
  { key: "sexe",             label: "Sexe",              type: "select", options: [["M", "Masculin"], ["F", "Féminin"]] },
  { key: "date_naissance",   label: "Date de naissance", type: "date" },
  { key: "lieu_naissance",   label: "Lieu de naissance", type: "text" },
  { key: "telephone",        label: "Téléphone",         type: "tel",   placeholder: "+221 77 000 00 00" },
  { key: "email",            label: "E-mail",            type: "email", placeholder: "exemple@ecole.sn" },
  { key: "adresse",          label: "Adresse",           type: "text" },
  { key: "fonction",         label: "Fonction",          type: "text",  placeholder: "Ex. Maître titulaire" },
  { key: "diplome",          label: "Diplôme",           type: "text",  placeholder: "Ex. CEAP, CFEE, Licence…" },
  { key: "matricule_pro",    label: "Matricule professionnel", type: "text" },
  { key: "experience_annees", label: "Années d'expérience", type: "number" },
  { key: "langue_pref",      label: "Langue préférée",   type: "select", options: [["fr", "Français"], ["wo", "Wolof"], ["en", "English"]] },
];

const NOTIF_CHANNELS = [
  { key: "in_app",   label: "Dans l'application", disabled: false },
  { key: "email",    label: "E-mail",             disabled: false },
  { key: "sms",      label: "SMS",                disabled: true, badge: "Bientôt" },
  { key: "whatsapp", label: "WhatsApp",           disabled: true, badge: "Bientôt" },
];

const NOTIF_TOPICS = [
  { key: "messages_direction", label: "Messages de la direction" },
  { key: "absences_retards",   label: "Absences et retards" },
  { key: "evaluations",        label: "Évaluations" },
  { key: "bulletins",          label: "Bulletins" },
  { key: "paiements",          label: "Paiements et cotisations" },
  { key: "messages_parents",   label: "Messages des parents" },
  { key: "informations_admin", label: "Informations administratives" },
];

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initial = VALID_TABS.includes(urlTab) ? urlTab : "profile";
  const [tab, setTab] = useState(initial);

  const [schoolAccess, setSchoolAccess] = useState(null);

  useEffect(() => {
    api.get("/settings/school-access").then(({ data }) => setSchoolAccess(data)).catch(() => setSchoolAccess({ can_view: false, can_edit: false }));
  }, []);

  useEffect(() => {
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== tab) setTab(urlTab);
  }, [urlTab, tab]);

  const changeTab = (v) => {
    setTab(v);
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const canSeeSchool = schoolAccess?.can_view === true;

  // Si l'utilisateur navigue vers ?tab=school sans les droits → forcer vers "profile"
  useEffect(() => {
    if (schoolAccess && !canSeeSchool && tab === "school") {
      changeTab("profile");
      toast.error("Vous n'êtes pas autorisé à modifier les informations de cet établissement. Contactez votre directeur.");
    }
  }, [schoolAccess, tab, canSeeSchool, changeTab]);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Paramètres</h1>
        <p className="text-sm text-[#5C736A]">Gérez votre profil, la sécurité et les préférences de votre compte.</p>
      </div>

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="w-full overflow-x-auto flex justify-start">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1" /> Mon profil</TabsTrigger>
          {canSeeSchool && <TabsTrigger value="school" data-testid="tab-school"><Building2 className="w-4 h-4 mr-1" /> Mon école</TabsTrigger>}
          <TabsTrigger value="security" data-testid="tab-security"><Shield className="w-4 h-4 mr-1" /> Sécurité</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell className="w-4 h-4 mr-1" /> Notifications</TabsTrigger>
          <TabsTrigger value="danger" data-testid="tab-danger"><AlertTriangle className="w-4 h-4 mr-1" /> Supprimer</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-5"><ProfileTab onSaved={refresh} /></TabsContent>
        {canSeeSchool && (
          <TabsContent value="school" className="mt-5">
            <SchoolTab schoolAccess={schoolAccess} />
          </TabsContent>
        )}
        <TabsContent value="security" className="mt-5"><SecurityTab /></TabsContent>
        <TabsContent value="notifications" className="mt-5"><NotificationsTab /></TabsContent>
        <TabsContent value="danger" className="mt-5">
          <DangerTab
            onDeleted={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------------------------------------------------------
 * PROFILE TAB
 * ------------------------------------------------------------------ */
function ProfileTab({ onSaved }) {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [avatar, setAvatar] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/me");
      setMe(data);
      setAvatar(data.avatar_base64 || null);
      setForm(pickProfileFields(data));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = () => { setForm(pickProfileFields(me)); setAvatar(me.avatar_base64 || null); setEditing(true); };
  const cancel = () => { setForm(pickProfileFields(me)); setAvatar(me.avatar_base64 || null); setEditing(false); };

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 700 * 1024) {
      toast.error("La photo ne doit pas dépasser 700 Ko");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (avatar !== me?.avatar_base64) payload.avatar_base64 = avatar || "";
      const { data } = await api.patch("/settings/profile", payload);
      setMe(data);
      setAvatar(data.avatar_base64 || null);
      setEditing(false);
      toast.success("Profil mis à jour");
      if (onSaved) await onSaved();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  if (!me) return <div className="text-sm text-[#5C736A]">Chargement…</div>;

  const initials = (me.prenom || me.name || me.username || "?").slice(0, 1).toUpperCase();

  return (
    <div className="space-y-5">
      {/* Header card with avatar */}
      <div className="ek-card p-5 flex flex-wrap items-center gap-5">
        <div className="relative">
          {avatar ? (
            <img src={avatar} alt="avatar" className="w-24 h-24 rounded-full object-cover border-4 border-[#E8F2EF]" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#1F7A63] text-white grid place-items-center text-3xl font-bold border-4 border-[#E8F2EF]">
              {initials}
            </div>
          )}
          {editing && (
            <div className="absolute -bottom-1 -right-1 flex gap-1">
              <label className="w-8 h-8 rounded-full bg-white shadow border grid place-items-center cursor-pointer" title="Changer la photo">
                <Camera className="w-4 h-4 text-[#1F7A63]" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="avatar-input" />
              </label>
              {avatar && (
                <button
                  type="button"
                  className="w-8 h-8 rounded-full bg-white shadow border grid place-items-center"
                  title="Supprimer la photo"
                  onClick={() => setAvatar(null)}
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-xl font-bold text-[#1A2E28]">{me.name}</div>
          <div className="text-sm text-[#5C736A]">{me.username} · {me.role}</div>
          {me.classes_assigned?.length > 0 && (
            <div className="mt-1 text-xs text-[#5C736A]">
              Classes : {me.classes_assigned.map((c) => c.nom).join(", ")}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {!editing && (
            <Button onClick={startEdit} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="profile-edit-btn">
              Modifier
            </Button>
          )}
          {editing && (
            <>
              <Button variant="outline" onClick={cancel}><X className="w-4 h-4 mr-1" /> Annuler</Button>
              <Button onClick={save} disabled={saving} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="profile-save-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Form fields */}
      <div className="ek-card p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PROFILE_FIELDS.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              value={form[f.key]}
              displayValue={me[f.key]}
              editing={editing}
              onChange={(v) => setForm({ ...form, [f.key]: v })}
            />
          ))}
        </div>

        {/* Preference: show phone on student cards (maître only) */}
        {user?.role === "maitre" && (
          <div className="mt-5 pt-4 border-t border-[#E2E8F0]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-[#1A2E28]">Afficher mon numéro sur les cartes scolaires</div>
                <p className="text-xs text-[#5C736A] mt-0.5">
                  Si activé, votre téléphone apparaîtra sur les cartes scolaires imprimées de vos élèves.
                </p>
              </div>
              <Switch
                checked={!!form.show_phone_on_cards}
                onCheckedChange={(v) => setForm({ ...form, show_phone_on_cards: !!v })}
                disabled={!editing}
                data-testid="show-phone-on-cards"
              />
            </div>
          </div>
        )}

        {/* Account username (read only) */}
        <div className="mt-6 pt-4 border-t border-[#E2E8F0] text-sm text-[#5C736A]">
          <Info className="inline w-4 h-4 mr-1" />
          Votre identifiant de connexion (<span className="font-semibold text-[#1A2E28]">{me.username}</span>) ne peut pas être modifié depuis cet écran. Contactez un administrateur si besoin.
        </div>
      </div>
    </div>
  );
}

function pickProfileFields(me) {
  const out = {};
  for (const f of PROFILE_FIELDS) out[f.key] = me?.[f.key] ?? "";
  out.show_phone_on_cards = !!me?.show_phone_on_cards;
  return out;
}

function FieldRow({ field, value, displayValue, editing, onChange }) {
  const emptyText = "—";
  return (
    <div>
      <Label className="text-xs text-[#5C736A]">{field.label}</Label>
      {editing ? (
        field.type === "select" ? (
          <Select value={value || ""} onValueChange={onChange}>
            <SelectTrigger data-testid={`field-${field.key}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              {field.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        ) : (
          <Input
            type={field.type}
            value={value || ""}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) || 0 : e.target.value)}
            data-testid={`field-${field.key}`}
          />
        )
      ) : (
        <div className="mt-1 text-[#1A2E28]">
          {displayValue || <span className="text-[#94A3B8]">{emptyText}</span>}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------
 * SCHOOL TAB
 * ------------------------------------------------------------------ */
const SCHOOL_FIELDS_A = [
  { key: "nom",                label: "Nom de l'école",   type: "text" },
  { key: "devise",             label: "Devise / slogan",  type: "text" },
  { key: "type_etablissement", label: "Type d'établissement", type: "text",
    placeholder: "Ex. École primaire, Collège…" },
  { key: "statut",             label: "Statut",           type: "select",
    options: [["public", "Public"], ["prive", "Privé"], ["communautaire", "Communautaire"], ["franco-arabe", "Franco-arabe"], ["autre", "Autre"]] },
  { key: "ia",                 label: "Académie (IA)",    type: "text" },
  { key: "ief",                label: "IEF",              type: "text" },
  { key: "region",             label: "Région",           type: "text" },
  { key: "departement",        label: "Département",      type: "text" },
  { key: "commune",            label: "Commune",          type: "text" },
  { key: "ville",              label: "Ville / village",  type: "text" },
  { key: "adresse",            label: "Adresse complète", type: "text" },
  { key: "telephone",          label: "Téléphone",        type: "tel" },
  { key: "email",              label: "E-mail",           type: "email" },
  { key: "annee_scolaire",     label: "Année scolaire",   type: "text", placeholder: "2025-2026" },
  { key: "responsable_nom",    label: "Nom du responsable", type: "text" },
  { key: "responsable_fonction", label: "Fonction du responsable", type: "text" },
  { key: "num_autorisation",   label: "N° d'autorisation",  type: "text" },
];

const SCHOOL_TEXTAREAS = [
  { key: "infos_bulletin",  label: "Informations à afficher sur les bulletins" },
  { key: "infos_recu",      label: "Informations à afficher sur les reçus" },
  { key: "infos_documents", label: "Informations à afficher sur les documents officiels" },
];

const SCHOOL_IMAGES = [
  { key: "logo_base64",                 label: "Logo de l'école" },
  { key: "signature_responsable_base64", label: "Signature du responsable" },
  { key: "cachet_base64",               label: "Cachet de l'établissement" },
];

function SchoolTab({ schoolAccess }) {
  const [school, setSchool] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [images, setImages] = useState({});
  const [saving, setSaving] = useState(false);
  const canEdit = !!schoolAccess?.can_edit;
  const schoolId = schoolAccess?.school_id;

  const load = async () => {
    if (!schoolId) { setSchool(null); return; }
    try {
      const { data } = await api.get(`/schools/${schoolId}`);
      setSchool(data);
      setForm(pickSchoolFields(data));
      setImages(pickSchoolImages(data));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, [schoolId]);

  if (!schoolId) {
    return (
      <div className="ek-card p-6 text-center">
        <Building2 className="w-8 h-8 mx-auto text-[#5C736A] mb-2" />
        <div className="font-semibold">Aucune école configurée</div>
        <p className="text-sm text-[#5C736A] mt-1">
          {schoolAccess?.can_view ? "Créez d'abord votre école dans la page « Mon école »." :
            "Vous n'êtes pas autorisé à modifier les informations de cet établissement. Contactez votre directeur."}
        </p>
      </div>
    );
  }
  if (!school) return <div className="text-sm text-[#5C736A]">Chargement…</div>;

  const onFile = (key, file) => {
    if (!file) return;
    if (file.size > 900 * 1024) { toast.error("L'image ne doit pas dépasser 900 Ko"); return; }
    const reader = new FileReader();
    reader.onload = () => setImages({ ...images, [key]: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, ...images };
      const { data } = await api.patch(`/schools/${schoolId}`, payload);
      setSchool(data);
      setForm(pickSchoolFields(data));
      setImages(pickSchoolImages(data));
      setEditing(false);
      toast.success("Informations de l'école mises à jour");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const cancel = () => { setForm(pickSchoolFields(school)); setImages(pickSchoolImages(school)); setEditing(false); };

  return (
    <div className="space-y-5">
      <div className="ek-card p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <div className="text-xl font-bold text-[#1A2E28]">{school.nom}</div>
          <div className="text-sm text-[#5C736A]">{school.annee_scolaire || "—"} · {school.region || "—"} · {school.commune || "—"}</div>
        </div>
        {canEdit && !editing && (
          <Button onClick={() => setEditing(true)} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="school-edit-btn">
            Modifier
          </Button>
        )}
        {canEdit && editing && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={cancel}><X className="w-4 h-4 mr-1" /> Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="school-save-btn">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="ek-card p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {SCHOOL_FIELDS_A.map((f) => (
            <FieldRow
              key={f.key}
              field={f}
              value={form[f.key]}
              displayValue={school[f.key]}
              editing={editing && canEdit}
              onChange={(v) => setForm({ ...form, [f.key]: v })}
            />
          ))}
        </div>
      </div>

      {/* Textareas */}
      <div className="ek-card p-5 space-y-4">
        {SCHOOL_TEXTAREAS.map((f) => (
          <div key={f.key}>
            <Label className="text-xs text-[#5C736A]">{f.label}</Label>
            {editing && canEdit ? (
              <Textarea rows={2} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            ) : (
              <div className="mt-1 text-[#1A2E28] whitespace-pre-wrap">
                {school[f.key] || <span className="text-[#94A3B8]">—</span>}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Images */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SCHOOL_IMAGES.map((img) => (
          <div key={img.key} className="ek-card p-4 text-center">
            <div className="text-xs text-[#5C736A] mb-2">{img.label}</div>
            {images[img.key] ? (
              <img src={images[img.key]} alt={img.label} className="max-h-32 mx-auto object-contain" />
            ) : (
              <div className="h-32 grid place-items-center text-[#94A3B8] text-sm">Aucune image</div>
            )}
            {editing && canEdit && (
              <div className="mt-2 flex justify-center gap-2">
                <label className="text-xs text-[#1F7A63] font-semibold cursor-pointer">
                  Changer
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(img.key, e.target.files?.[0])} />
                </label>
                {images[img.key] && (
                  <button type="button" className="text-xs text-red-600" onClick={() => setImages({ ...images, [img.key]: "" })}>Retirer</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function pickSchoolFields(s) {
  const out = {};
  for (const f of [...SCHOOL_FIELDS_A, ...SCHOOL_TEXTAREAS]) out[f.key] = s?.[f.key] ?? "";
  return out;
}
function pickSchoolImages(s) {
  const out = {};
  for (const f of SCHOOL_IMAGES) out[f.key] = s?.[f.key] ?? "";
  return out;
}

/* --------------------------------------------------------------------
 * SECURITY TAB
 * ------------------------------------------------------------------ */
function SecurityTab() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [me, setMe] = useState(null);
  const [sessions, setSessions] = useState([]);

  const load = async () => {
    try {
      const [meR, sessR] = await Promise.all([
        api.get("/settings/me"),
        api.get("/settings/sessions?limit=5"),
      ]);
      setMe(meR.data);
      setSessions(sessR.data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const rules = useMemo(() => ({
    length: next.length >= 8,
    letter: /[A-Za-z]/.test(next),
    digit:  /\d/.test(next),
    match:  next && next === confirm,
    diff:   next && next !== current,
  }), [next, confirm, current]);

  const canSubmit = rules.length && rules.letter && rules.digit && rules.match && rules.diff && current;

  const submit = async () => {
    if (!canSubmit) { toast.error("Vérifiez les règles du nouveau mot de passe"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/settings/change-password", { current_password: current, new_password: next });
      toast.success(data.message || "Mot de passe modifié");
      setCurrent(""); setNext(""); setConfirm("");
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const logoutAll = async () => {
    if (!window.confirm("Se déconnecter de tous les appareils ? Vous devrez vous reconnecter partout ailleurs.")) return;
    try {
      await api.post("/settings/logout-all-devices");
      toast.success("Déconnexion effectuée sur tous les autres appareils");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div className="ek-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#1F7A63]" />
          <h2 className="font-bold text-lg">Changer le mot de passe</h2>
        </div>

        <PasswordField label="Mot de passe actuel" value={current} onChange={setCurrent} show={showCur} onToggle={() => setShowCur(!showCur)} testid="pw-current" />
        <PasswordField label="Nouveau mot de passe" value={next} onChange={setNext} show={showNew} onToggle={() => setShowNew(!showNew)} testid="pw-new" />
        <PasswordField label="Confirmer le nouveau mot de passe" value={confirm} onChange={setConfirm} show={showNew} onToggle={() => setShowNew(!showNew)} testid="pw-confirm" />

        <div className="space-y-1 text-xs">
          <Rule ok={rules.length} label="Au moins 8 caractères" />
          <Rule ok={rules.letter} label="Au moins une lettre" />
          <Rule ok={rules.digit}  label="Au moins un chiffre" />
          <Rule ok={rules.diff}   label="Différent du mot de passe actuel" />
          <Rule ok={rules.match}  label="Correspond à la confirmation" />
        </div>

        <Button onClick={submit} disabled={!canSubmit || saving} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white w-full" data-testid="pw-save-btn">
          {saving ? "Enregistrement…" : "Modifier le mot de passe"}
        </Button>

        {me?.password_changed_at && (
          <div className="text-xs text-[#5C736A]">
            Dernière modification : {new Date(me.password_changed_at).toLocaleString("fr-FR")}
          </div>
        )}
      </div>

      <div className="ek-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <LogOut className="w-5 h-5 text-[#1F7A63]" />
          <h2 className="font-bold text-lg">Sessions actives</h2>
        </div>

        <Button variant="outline" onClick={logoutAll} className="w-full" data-testid="logout-all-btn">
          <LogOut className="w-4 h-4 mr-1" /> Se déconnecter de tous les appareils
        </Button>

        <div>
          <div className="text-sm font-semibold mb-2">Historique récent des connexions</div>
          <div className="space-y-2">
            {sessions.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-3 border border-[#E2E8F0] rounded-lg p-2 text-xs">
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                <div className="flex-1 min-w-[160px]">
                  <div className="font-semibold text-[#1A2E28]">
                    {new Date(s.at).toLocaleString("fr-FR")}
                  </div>
                  <div className="text-[#5C736A] truncate" title={s.user_agent}>{s.user_agent || "—"}</div>
                </div>
                <div className="text-[#5C736A]">{s.ip}</div>
              </div>
            ))}
            {sessions.length === 0 && <div className="text-xs text-[#5C736A]">Aucun historique disponible.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordField({ label, value, onChange, show, onToggle, testid }) {
  return (
    <div>
      <Label className="text-xs text-[#5C736A]">{label}</Label>
      <div className="relative">
        <Input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <button type="button" onClick={onToggle} className="absolute right-2 top-2 text-[#5C736A]">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <div className={`flex items-center gap-1 ${ok ? "text-green-700" : "text-[#94A3B8]"}`}>
      <CheckCircle2 className={`w-3 h-3 ${ok ? "opacity-100" : "opacity-30"}`} />
      {label}
    </div>
  );
}

/* --------------------------------------------------------------------
 * NOTIFICATIONS TAB
 * ------------------------------------------------------------------ */
function NotificationsTab() {
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/settings/notifications");
      setPrefs(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  if (!prefs) return <div className="text-sm text-[#5C736A]">Chargement…</div>;

  const toggle = (k) => setPrefs({ ...prefs, [k]: !prefs[k] });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/notifications", prefs);
      setPrefs(data);
      toast.success("Préférences enregistrées");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="ek-card p-5">
        <h2 className="font-bold text-lg mb-3">Canaux</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {NOTIF_CHANNELS.map((c) => (
            <div key={c.key} className={`flex items-center justify-between border border-[#E2E8F0] rounded-lg p-3 ${c.disabled ? "opacity-60" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-[#1A2E28]">{c.label}</span>
                {c.badge && <Badge variant="outline" className="text-[10px]">{c.badge}</Badge>}
              </div>
              <Switch checked={!!prefs[c.key]} disabled={c.disabled} onCheckedChange={() => toggle(c.key)} data-testid={`notif-channel-${c.key}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="ek-card p-5">
        <h2 className="font-bold text-lg mb-3">Sujets</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {NOTIF_TOPICS.map((t) => (
            <div key={t.key} className="flex items-center justify-between border border-[#E2E8F0] rounded-lg p-3">
              <span className="font-medium text-[#1A2E28]">{t.label}</span>
              <Switch checked={!!prefs[t.key]} onCheckedChange={() => toggle(t.key)} data-testid={`notif-topic-${t.key}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="notif-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Enregistrement…" : "Enregistrer mes préférences"}
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------
 * DANGER TAB — suppression compte
 * ------------------------------------------------------------------ */
function DangerTab({ onDeleted }) {
  const { user } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isTeacherAttached = user?.role === "maitre" && !user?.is_independent;
  const isMaitre = user?.role === "maitre";
  const REQUIRED = "SUPPRIMER MON COMPTE";
  const canConfirm = password.length > 0 && confirmation === REQUIRED;
  const [request, setRequest] = useState(null);
  const [motif, setMotif] = useState("");

  const loadRequest = () => api.get("/settings/account/deletion-request").then(({ data }) => setRequest(data)).catch(() => {});
  useEffect(() => { if (isMaitre) loadRequest(); /* eslint-disable-next-line */ }, [isMaitre]);
  const pending = request?.status === "pending";

  const cancelRequest = async () => {
    try { await api.delete("/settings/account/deletion-request"); toast.success("Demande annulée"); loadRequest(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const submit = async () => {
    if (!canConfirm) return;
    if (isMaitre) {
      setDeleting(true);
      try {
        await api.post("/settings/account/deletion-request", { current_password: password, confirmation, motif });
        toast.success("Demande envoyée à l'administrateur");
        setDialogOpen(false);
        loadRequest();
      } catch (e) { toast.error(formatError(e)); }
      finally { setDeleting(false); }
      return;
    }
    if (!window.confirm("Confirmez-vous la suppression définitive de votre compte ?")) return;
    setDeleting(true);
    try {
      await api.delete("/settings/account", { data: { current_password: password, confirmation } });
      toast.success("Votre compte a été fermé.");
      setDialogOpen(false);
      if (onDeleted) await onDeleted();
    } catch (e) { toast.error(formatError(e)); }
    finally { setDeleting(false); }
  };

  return (
    <div className="ek-card p-5 border-2 border-red-200 bg-red-50/40 space-y-4" data-testid="danger-zone">
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-6 h-6 text-red-600" />
        <h2 className="font-bold text-lg text-red-800">Zone dangereuse</h2>
      </div>

      <p className="text-sm text-red-900">
        La suppression de votre compte est <strong>définitive</strong>. Vous risquez de perdre l&apos;accès à vos classes,
        élèves, notes, bulletins, documents et autres données. Cette action ne peut pas être annulée.
      </p>

      {isTeacherAttached && (
        <div className="rounded-lg bg-white/70 border border-red-200 p-3 text-xs text-red-800">
          Votre compte est encore rattaché à un établissement. Certaines données scolaires appartiennent à l&apos;école et
          ne seront pas supprimées. Votre accès personnel sera fermé.
        </div>
      )}

      {!isTeacherAttached && user?.role === "maitre" && (
        <div className="rounded-lg bg-white/70 border border-red-200 p-3 text-xs text-red-800">
          Avant de supprimer votre compte, pensez à télécharger vos bulletins, listes d&apos;élèves et rapports depuis les
          pages dédiées.
        </div>
      )}

      {isMaitre && (
        <div className="rounded-lg bg-white border border-[#E2E8F0] p-3 text-sm text-[#1A2E28]" data-testid="deletion-request-info">
          En tant qu&apos;enseignant, vous ne pouvez pas supprimer votre compte vous-même : votre demande sera examinée par l&apos;administrateur, qui pourra désactiver votre accès.
          {request && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="deletion-request-status">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                pending ? "bg-[#FFF7D6] text-[#7A6100]" : request.status === "approved" ? "bg-red-100 text-red-700" : "bg-slate-100 text-[#5C736A]"
              }`}>
                {{ pending: "Demande en attente", approved: "Demande acceptée", rejected: "Demande refusée", cancelled: "Demande annulée" }[request.status]}
              </span>
              <span className="text-xs text-[#5C736A]">envoyée le {new Date(request.created_at).toLocaleDateString("fr-FR")}</span>
              {pending && <Button size="sm" variant="outline" onClick={cancelRequest} data-testid="cancel-deletion-request-btn">Annuler la demande</Button>}
            </div>
          )}
        </div>
      )}

      <Button
        variant="destructive"
        disabled={pending}
        onClick={() => { setPassword(""); setConfirmation(""); setMotif(""); setDialogOpen(true); }}
        className="bg-red-600 hover:bg-red-700 text-white"
        data-testid="open-delete-dialog-btn"
      >
        <Trash2 className="w-4 h-4 mr-1" /> {isMaitre ? "Demander la suppression de mon compte" : "Supprimer mon compte"}
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-700">{isMaitre ? "Demander la suppression du compte" : "Confirmer la suppression du compte"}</DialogTitle>
            <DialogDescription>
              {isMaitre ? "Votre demande sera transmise à l'administrateur pour validation." : "Cette action est irréversible. Elle fermera votre accès à EDUKATI."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {isMaitre && (
              <div>
                <Label className="text-xs text-[#5C736A]">Motif (optionnel)</Label>
                <Textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder="Ex. mutation, départ à la retraite…" data-testid="delete-motif-input" />
              </div>
            )}
            <div>
              <Label className="text-xs text-[#5C736A]">Mot de passe actuel</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="delete-password-input"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-2 text-[#5C736A]">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#5C736A]">Tapez exactement <span className="font-mono font-bold">{REQUIRED}</span></Label>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                data-testid="delete-confirmation-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
            <Button
              onClick={submit}
              disabled={!canConfirm || deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {deleting ? "Envoi…" : isMaitre ? "Envoyer la demande" : "Confirmer la suppression définitive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
